import type { LangProps } from '@app/lang/types'
import { store } from '@app/store'
import { getRouterBasename } from './utils'

const urls = {
  root: '/',
  faqs: 'faqs',
  quiz: 'quiz',
  orderComplete: 'order-complete',
  privacy: 'privacy',
  terms: 'terms',
  cookies: 'cookies',
  backoffice: 'admin',
  auth: 'authentication',
  // Absolute path to the signout endpoint
  signOut: '/authentication/signout',
  notFound: '404',
  accessDenied: 'admin/access-denied',
  closed: 'closed',
}

export const quizUrls = {
  customer: '/customer',
  model: '/model',
  purchase: '/purchase',
  valuation: '/valuation',
  shipping: '/shipping',
  payment: '/payment',
}

/**
 * Paths that should ignore the dynamic language segment and related redirects
 */
export const staticPaths = [urls.backoffice, urls.auth]

/**
 * Replaces the language in the given URL with the specified language.
 * If the base URL is not the root, it is removed from the constructed URL.
 *
 * @param url - The URL to modify.
 * @param language - The language segment to replace in the URL.
 * @returns The modified URL with the language segment replaced.
 */
export const replaceLanguage = (url: string, language: string) => {
  let workingUrl = url
  const state = store.getState()
  const currentLanguage = state.settings.language

  // If the base URL is not the root, remove it from the constructed URL
  const basename = getRouterBasename()

  if (basename !== '/') {
    workingUrl = workingUrl.replace(basename, '')
  }

  return workingUrl.replace(currentLanguage, language)
}

export const getDocumentTitle = (pathname: string, language: LangProps) => {
  // Strip the slashes and empty strings
  const pathParts = pathname.split('/').filter(Boolean)

  // Ignore the language segment
  const path = pathParts.length === 1 ? '/' : pathParts[1]

  const dictionary = {
    [urls.root]: language.document_title_home,
    [urls.faqs]: language.document_title_faq,
    [urls.quiz]: language.document_title_quiz,
    [urls.privacy]: language.document_title_privacy,
    [urls.terms]: language.document_title_terms,
    [urls.cookies]: language.document_title_cookies,
    [urls.backoffice]: language.document_title_backoffice,
    [urls.signOut]: language.document_title_signOut,
    [urls.notFound]: language.document_title_notFound,
  }

  return dictionary[path] ?? language.document_title_notFound
}

export default urls
